import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";

const NAV = [
  { label: "Why Veda", href: "#why" },
  { label: "Included", href: "#value" },
  { label: "How To Use", href: "#how" },
  { label: "FAQ", href: "#faq" },
];

export const Header = () => {
  const { count, setCartOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            data-testid="logo-btn"
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center"
          >
            <img
              src="/assets/veda_logo_mark.png"
              alt="Veda Vitals"
              className="h-7 md:h-9 w-auto"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <button
                key={n.href}
                data-testid={`nav-${n.href.replace("#", "")}`}
                onClick={() => go(n.href)}
                className="text-sm text-vmuted hover:text-white transition-colors duration-300 uppercase tracking-[0.12em]"
              >
                {n.label}
              </button>
            ))}
            <Link
              to="/product"
              data-testid="nav-product"
              className="text-sm text-gold hover:text-gold-light transition-colors duration-300 uppercase tracking-[0.12em]"
            >
              Product
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="cart-btn"
              onClick={() => setCartOpen(true)}
              className="relative h-11 w-11 grid place-items-center rounded-full glass hover:border-gold/40 transition-colors duration-300"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-5 w-5 text-white" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-gold text-obsidian text-[11px] font-semibold grid place-items-center gold-glow"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden h-11 w-11 grid place-items-center rounded-full glass"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {open ? (
                <X className="h-5 w-5 text-white" strokeWidth={1.5} />
              ) : (
                <Menu className="h-5 w-5 text-white" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden glass border-t border-white/5"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => go(n.href)}
                data-testid={`mnav-${n.href.replace("#", "")}`}
                className="text-left text-vmuted hover:text-white transition-colors uppercase tracking-[0.12em] text-sm"
              >
                {n.label}
              </button>
            ))}
            <Link
              to="/product"
              onClick={() => setOpen(false)}
              data-testid="mnav-product"
              className="text-left text-gold uppercase tracking-[0.12em] text-sm"
            >
              Product
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
